import { CKButton, CKDrawer } from 'clearkit';
import { useEffect, useState } from 'react';

import { useCurrentUser } from '~/components/profile/useCurrentUser';
import history from '~/lib/history';
import { trackUpgradeFormSubmitted } from '~/lib/segmentAnalytics.js';

import { ContactValues, FormContact } from './FormContact';
import UpgradeAccountComplete from './UpgradeAccountComplete';

type ConnectedUpgradeAccountProps = {
  backPath?: string;
  onClose?: () => void;
};

const isValid = (values: Record<string, unknown>) => {
  if (values.other && !values.message) {
    return false;
  }

  return values.email !== '' && values.name !== '';
};

const UpgradeAccountForm = ({
  backPath,
  onClose,
}: ConnectedUpgradeAccountProps) => {
  const { user, loading } = useCurrentUser();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [values, setValues] = useState<ContactValues>({
    email: '',
    name: '',
    credits: false,
    enrichmentData: false,
    prospecting: false,
  });

  const handleChange = (key: string) => (value: string | boolean) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    const { email, name, message } = values;
    const interests = ['prospecting', 'enrichmentData', 'credits'].filter(
      (key) => values[key as keyof typeof values] === true,
    );
    trackUpgradeFormSubmitted({ email, name, message, interests });
    setHasSubmitted(true);
  };

  const valid = isValid(values);

  useEffect(() => {
    if (user?.name && user?.email) {
      setValues({
        ...values,
        email: user?.email || '',
        name: user?.name || '',
      });
    }
  }, [user]);

  return (
    <>
      <CKDrawer.Header>
        <CKDrawer.Heading>
          Get in touch to upgrade your Clearbit plan
        </CKDrawer.Heading>
      </CKDrawer.Header>
      <CKDrawer.Body className="p-6 bg-gray-0">
        {hasSubmitted ? (
          <UpgradeAccountComplete email={values.email} />
        ) : (
          <FormContact
            isLoading={loading}
            onChange={handleChange}
            values={values}
          />
        )}
      </CKDrawer.Body>
      {!hasSubmitted ? (
        <CKDrawer.Footer className="gap-3">
          <CKButton
            onClick={() => {
              backPath ? history.push(backPath) : onClose?.();
            }}
            variant="simple"
          >
            Cancel
          </CKButton>
          <CKButton
            isDisabled={!valid}
            onClick={handleSubmit}
            type="submit"
            variant="bold"
            variantColor="green"
          >
            Submit contact form
          </CKButton>
        </CKDrawer.Footer>
      ) : null}
    </>
  );
};

export default UpgradeAccountForm;
